/* ===========================================================
   pages/Music.jsx — Music Protocol, prototype (his ask, 2026-11-04:
   "start Music Protocol", music.weatherprotocol.org, his account only)

   From his plan (Updates doc Tab 1), no libraries:
     Player         search free music (Audius, Internet Archive), your own links
                    and uploads, visualizers, lyrics that follow the song
     Weather Radio  endless music from the weather outside (music/radio.js)
     Song Maker     a 16-step grid: tap squares, press play, save songs
   =========================================================== */

import { useEffect, useRef, useState } from 'preact/hooks';
import { session, docs } from '../state.js';
import { api } from '../lib/api.js';
import { openApp } from '../lib/hosts.js';
import { Icon, IconButton, Button, toast, promptDialog, confirmDialog } from '../ui/ui.jsx';
import { Proto } from '../ui/proto.jsx';
import { TopBar } from './Home.jsx';
import { WxBar } from '../ui/AppBar.jsx';
import { audio, tone, kick, snare, hat, clock, midi, setVolume } from '../music/synth.js';
import { startRadio, moodOf } from '../music/radio.js';
import { VISUALIZERS, parseLRC } from '../music/visualizers.js';
import { upload } from './NotesDrive.jsx';

const TABS = [['player', 'Player', 'play'], ['radio', 'Weather Radio', 'cloudSun'], ['maker', 'Song Maker', 'music']];

export function MusicPage() {
  const s = session.value;
  const [tab, setTab] = useState(() => { try { return localStorage.getItem('dp:music:tab') || 'player'; } catch { return 'player'; } });
  const pick = t => { setTab(t); try { localStorage.setItem('dp:music:tab', t); } catch { /* private */ } };
  if (!s.owner) {
    return (
      <div class="home">
        <TopBar app="music" />
        <main id="main" class="home-main"><div class="empty"><Proto size={72} /><p>Music Protocol is still being built. Check back soon!</p>
          <Button onClick={() => openApp('docs')}>Back to Docs</Button></div></main>
      </div>
    );
  }
  return (
    <div class="home music">
      <TopBar app="music" />
      <WxBar />
      <main id="main" class="home-main">
        <div class="mu-head">
          <h1 class="mu-title">Music Protocol <span class="beta-chip">Prototype</span></h1>
          <div class="segmented mu-tabs" role="tablist">
            {TABS.map(([id, label, icon]) => (
              <button type="button" role="tab" key={id} aria-selected={tab === id} class={`seg ${tab === id ? 'on' : ''}`} onClick={() => pick(id)}><Icon name={icon} size={15} />{label}</button>
            ))}
          </div>
        </div>
        {tab === 'radio' && <Radio />}
        {tab === 'maker' && <Maker />}
        {(tab === 'player' || tab === 'library') && <Player />}
      </main>
    </div>
  );
}

/* ---------- Weather Radio ---------- */

async function weatherNow() {
  const where = await api('/api/where');
  const w = where?.wx;
  if (!w) throw new Error('No weather where you are right now.');
  // the radio thinks in °F and mph
  const us = w.units === 'us';
  return { name: where.name, code: w.code, isDay: w.isDay, temp: us ? w.temp : w.temp * 9 / 5 + 32, wind: us ? w.wind : w.wind / 1.609, rain: w.rain };
}

const PRESETS = [
  ['Live', null], ['Sunshine', { code: 0, isDay: true, temp: 78, wind: 6 }], ['Breezy', { code: 1, isDay: true, temp: 65, wind: 18 }],
  ['Rain', { code: 63, isDay: true, temp: 55, wind: 8, rain: 90 }], ['Storm', { code: 95, isDay: false, temp: 72, wind: 25, rain: 100 }],
  ['Snow', { code: 73, isDay: true, temp: 25, wind: 4 }], ['Night', { code: 0, isDay: false, temp: 50, wind: 3 }],
];

function Radio() {
  const [wx, setWx] = useState(null);
  const [err, setErr] = useState(null);
  const [preset, setPreset] = useState('Live');
  const [playing, setPlaying] = useState(false);
  const [vol, setVol] = useState(0.7);
  const [bar, setBar] = useState(0);
  const radio = useRef(null);
  useEffect(() => { weatherNow().then(setWx, setErr); return () => radio.current?.stop(); }, []);
  const source = preset === 'Live' ? wx : { name: preset, ...PRESETS.find(p => p[0] === preset)[1] };
  const mood = source ? moodOf(source) : null;
  const stop = () => { radio.current?.stop(); radio.current = null; setPlaying(false); };
  const play = (src = source) => {
    if (!src) return;
    radio.current?.stop();
    audio(); setVolume(vol);
    radio.current = startRadio(src, { onBar: setBar });
    setPlaying(true);
  };
  return (
    <section class="mu-card mu-radio">
      <div class={`mu-orb ${playing ? 'on' : ''} mood-${mood?.scale || 'major'}`} style={{ '--beat': mood ? `${60 / mood.bpm}s` : '1s' }}>
        <button type="button" class="mu-play" aria-label={playing ? 'Stop' : 'Play'} onClick={() => (playing ? stop() : play())} disabled={!source}>
          <Icon name={playing ? 'stop' : 'play'} size={34} />
        </button>
      </div>
      <div class="mu-now">
        <span class="mono-label">{playing ? 'Now playing' : 'Ready'}</span>
        <h2>{mood ? `${mood.label}${source?.name && preset === 'Live' ? ` in ${source.name}` : ''}` : err ? 'No weather right now' : 'Listening to the sky…'}</h2>
        {mood && <p class="hint">{mood.bpm} bpm · {mood.key} · {['no drums', 'soft drums', 'drums', 'storm drums'][mood.drums]}{mood.snow ? ' · snow' : mood.rain ? ' · rain' : ''}{playing ? ` · bar ${bar + 1}` : ''}</p>}
        {err && preset === 'Live' && <p class="hint">{String(err.message || err)} Pick a sky below instead.</p>}
        <p class="hint">Made fresh from the weather, never the same twice: the sky picks the scale, the wind the tempo and drums, the temperature how bright it sounds.</p>
      </div>
      <div class="mu-presets" role="group" aria-label="Weather to play">
        {PRESETS.map(([name]) => (
          <button type="button" key={name} class={`chip ${preset === name ? 'on' : ''}`} onClick={() => { setPreset(name); if (playing) play(name === 'Live' ? wx : { name, ...PRESETS.find(p => p[0] === name)[1] }); }}>{name}</button>
        ))}
      </div>
      <label class="mu-vol"><Icon name="music" size={15} /> Volume
        <input type="range" min="0" max="1" step="0.05" value={vol} onInput={e => { const v = +e.target.value; setVol(v); setVolume(v); }} />
      </label>
    </section>
  );
}

/* ---------- Song Maker ---------- */

const STEPS = 16;
// tune rows: C major pentatonic, high to low; then drums
const TUNE = [84, 81, 79, 76, 74, 72, 69, 67, 64, 62, 60];
const ROWS = [...TUNE.map(n => ({ kind: 'tune', n })), { kind: 'bass', n: 36 }, { kind: 'snare' }, { kind: 'kick' }, { kind: 'hat' }];
const SONGS = 'dp:music:songs';
const blank = () => ROWS.map(() => Array(STEPS).fill(false));
const readSongs = () => { try { return JSON.parse(localStorage.getItem(SONGS) || '[]'); } catch { return []; } };
const NOTE = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
const rowLabel = r => (r.kind === 'tune' ? `${NOTE[r.n % 12]}${Math.floor(r.n / 12) - 1}` : r.kind === 'bass' ? 'Bass' : r.kind === 'snare' ? 'Snare' : r.kind === 'kick' ? 'Kick' : 'Hat');

function playRow(r, t, len, sound) {
  if (r.kind === 'tune') tone(midi(r.n), t, len * 0.9, sound === 'soft' ? { type: 'sine', gain: 0.14, release: 0.35, wet: 0.45 } : sound === 'retro' ? { type: 'square', gain: 0.07, release: 0.08, cutoff: 3000, wet: 0.2 } : { type: 'triangle', gain: 0.15, release: 0.2, wet: 0.3 });
  else if (r.kind === 'bass') tone(midi(r.n), t, len * 1.8, { type: 'sawtooth', gain: 0.12, release: 0.1, cutoff: 700, wet: 0 });
  else if (r.kind === 'kick') kick(t);
  else if (r.kind === 'snare') snare(t);
  else hat(t);
}

function Maker() {
  const [grid, setGrid] = useState(blank);
  const [bpm, setBpm] = useState(110);
  const [sound, setSound] = useState('bright');
  const [playing, setPlaying] = useState(false);
  const [at, setAt] = useState(-1);
  const [songs, setSongs] = useState(readSongs);
  const [name, setName] = useState('');
  const ck = useRef(null);
  const live = useRef({ grid, bpm, sound });
  live.current = { grid, bpm, sound };
  const paint = useRef(null);   // drag to paint several squares

  useEffect(() => () => ck.current?.stop(), []);
  useEffect(() => { ck.current?.setBpm(bpm); }, [bpm]);
  useEffect(() => { const up = () => { paint.current = null; }; addEventListener('pointerup', up); return () => removeEventListener('pointerup', up); }, []);

  const start = () => {
    audio();
    ck.current = clock({
      bpm, stepsPerBeat: 4,
      onStep(step, t) {
        const s = step % STEPS, { grid: g, bpm: b, sound: sd } = live.current;
        ROWS.forEach((r, i) => { if (g[i][s]) playRow(r, t, 60 / b / 4, sd); });
        setTimeout(() => setAt(s), Math.max(0, (t - audio().currentTime) * 1000));
      },
    });
    setPlaying(true);
  };
  const stop = () => { ck.current?.stop(); ck.current = null; setPlaying(false); setAt(-1); };
  const setCell = (i, j, v) => setGrid(g => g.map((row, a) => (a === i ? row.map((c, b) => (b === j ? v : c)) : row)));
  const press = (i, j) => {
    const v = !grid[i][j];
    paint.current = v;
    setCell(i, j, v);
    if (v) { audio(); playRow(ROWS[i], audio().currentTime + 0.01, 60 / bpm / 4, sound); }
  };
  const save = async () => {
    const n = (name || '').trim() || await promptDialog({ title: 'Name your song', label: 'Name', value: `Song ${songs.length + 1}` });
    if (!n?.trim()) return;
    const list = [{ id: Date.now().toString(36), name: n.trim().slice(0, 40), bpm, sound, grid }, ...songs.filter(x => x.name !== n.trim())].slice(0, 50);
    setSongs(list); setName(n.trim());
    try { localStorage.setItem(SONGS, JSON.stringify(list)); toast(`Saved "${n.trim()}".`); } catch { toast('Couldn\'t save on this device (storage full or private mode).', { kind: 'error' }); }
  };
  const load = song => { setGrid(ROWS.map((_, i) => song.grid[i] || Array(STEPS).fill(false))); setBpm(song.bpm); setSound(song.sound || 'bright'); setName(song.name); };
  const del = async song => {
    if (!await confirmDialog({ title: `Delete "${song.name}"?`, ok: 'Delete', danger: true })) return;
    const list = songs.filter(x => x.id !== song.id);
    setSongs(list); try { localStorage.setItem(SONGS, JSON.stringify(list)); } catch { /* fine */ }
  };
  const random = () => {
    // a quick idea: a tune that walks the scale, a beat, a bass line
    const g = blank();
    let row = 5;
    for (let j = 0; j < STEPS; j++) {
      if (Math.random() < 0.6) { row = Math.max(0, Math.min(TUNE.length - 1, row + Math.round((Math.random() - 0.5) * 4))); g[row][j] = true; }
      if (j % 4 === 0) g[TUNE.length][j] = true;
      if (j % 8 === 4) g[TUNE.length + 1][j] = true;
      if (j === 0 || j === 8 || (j === 10 && Math.random() < 0.5)) g[TUNE.length + 2][j] = true;
      if (j % 2 === 0) g[TUNE.length + 3][j] = true;
    }
    setGrid(g); setName('');
  };

  return (
    <section class="mu-card mu-maker">
      <div class="mu-bar">
        <Button kind="primary" icon={playing ? 'stop' : 'play'} onClick={() => (playing ? stop() : start())}>{playing ? 'Stop' : 'Play'}</Button>
        <label class="mu-bpm">Tempo <input type="range" min="60" max="180" value={bpm} onInput={e => setBpm(+e.target.value)} /> <b>{bpm}</b></label>
        <select class="input mu-sound" value={sound} onChange={e => setSound(e.target.value)} aria-label="Sound">
          <option value="bright">Bright</option><option value="soft">Soft</option><option value="retro">Retro</option>
        </select>
        <span class="grow" />
        <Button size="sm" icon="shuffle" onClick={random}>Surprise me</Button>
        <Button size="sm" icon="eraser" onClick={() => { setGrid(blank()); setName(''); }}>Clear</Button>
        <Button size="sm" icon="download" onClick={save}>Save</Button>
      </div>
      <div class="mu-grid" role="grid" aria-label="Song grid" style={{ '--steps': STEPS }}>
        {ROWS.map((r, i) => (
          <div class={`mu-row mu-${r.kind}`} role="row" key={i}>
            <span class="mu-lab">{rowLabel(r)}</span>
            {grid[i].map((on, j) => (
              <button type="button" role="gridcell" key={j} aria-pressed={on} aria-label={`${rowLabel(r)} step ${j + 1}`}
                class={`mu-cell ${on ? 'on' : ''} ${j === at ? 'now' : ''} ${j % 4 === 0 ? 'beat' : ''}`}
                onPointerDown={e => { e.preventDefault(); press(i, j); }}
                onPointerEnter={() => { if (paint.current != null && grid[i][j] !== paint.current) setCell(i, j, paint.current); }} />
            ))}
          </div>
        ))}
      </div>
      <p class="hint">Tap squares to add notes (drag to paint a row), then press Play. Songs save on this device.</p>
      {songs.length > 0 && (
        <div class="mu-songs">
          <h3 class="drive-h">Your songs</h3>
          <ul class="doclist">
            {songs.map(sg => (
              <li class="docrow" key={sg.id}>
                <button type="button" class="docrow-main" onClick={() => load(sg)}>
                  <span class="docrow-icon"><Icon name="music" size={20} /></span>
                  <span class="docrow-text"><span class="docrow-title">{sg.name}</span><span class="docrow-snippet">{sg.bpm} bpm · {sg.sound || 'bright'}</span></span>
                </button>
                <IconButton icon="trash" label={`Delete ${sg.name}`} onClick={() => del(sg)} />
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

/* ---------- Player (his plan, Updates doc Tab 1): search free music, your own links and
   uploads, a library of visualizers, and lyrics that follow the song line by line ---------- */

const AUDIO = /\.(mp3|m4a|aac|wav|ogg|oga|opus|flac|webm)$/i;
const SRC_NAME = { audius: 'Audius', archive: 'Internet Archive', drive: 'Your Drive', link: 'Your link' };
const fmtTime = s => (!Number.isFinite(s) || s <= 0 ? '0:00' : `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`);
const urlOf = t => (t.src === 'audius' || t.src === 'archive' ? `/api/music/stream?src=${t.src}&id=${encodeURIComponent(t.id)}` : t.url);
const VKEY = 'dp:music:viz';

let wired = null;   // the <audio> element already connected to the analyser (that can only happen once)
let analyser = null;

function Player() {
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [failed, setFailed] = useState([]);
  const [queue, setQueue] = useState([]);
  const [idx, setIdx] = useState(-1);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [dur, setDur] = useState(0);
  const [viz, setViz] = useState(() => { try { return localStorage.getItem(VKEY) || 'halo'; } catch { return 'halo'; } });
  const [lyrics, setLyrics] = useState(null);   // { lines, plain, state }
  const [link, setLink] = useState('');
  const [full, setFull] = useState(false);
  const el = useRef();        // same-origin songs: wired to the analyser for the visualizers
  const elLink = useRef();    // other sites' links: played as-is (the browser won't let us read their sound)
  const canvas = useRef();
  const lyricBox = useRef();
  const fileIn = useRef();
  const vizState = useRef({});
  const cur = idx >= 0 ? queue[idx] : null;
  const isLink = cur?.src === 'link';
  const player = () => (isLink ? elLink.current : el.current);

  const myFiles = docs.value.filter(d => d.kind === 'file' && !d.deletedAt && AUDIO.test(d.title || '') && d.snippet?.startsWith('/f/'))
    .map(d => ({ src: 'drive', id: d.id, title: d.title.replace(AUDIO, ''), artist: '', url: d.snippet, art: '', duration: 0 }));

  const search = async e => {
    e?.preventDefault();
    if (!q.trim()) return;
    setSearching(true);
    try { const r = await api(`/api/music/search?q=${encodeURIComponent(q.trim())}`); setResults(r.results); setFailed(r.failed || []); }
    catch (err) { toast(err, { kind: 'error' }); }
    setSearching(false);
  };

  const playTrack = (t, list = null) => {
    const l = list || [...queue.slice(0, idx + 1), t, ...queue.slice(idx + 1)];
    const i = list ? list.indexOf(t) : idx + 1;
    setQueue(l); setIdx(i);
  };
  const addToQueue = t => { setQueue(qq => [...qq, t]); if (idx < 0) setIdx(queue.length); toast(`Added "${t.title}" to the queue.`); };
  const next = () => { if (idx + 1 < queue.length) setIdx(idx + 1); else setPlaying(false); };
  const prev = () => { const p = player(); if (p && p.currentTime > 3) p.currentTime = 0; else if (idx > 0) setIdx(idx - 1); };

  // load + play the current song; fetch its lyrics
  useEffect(() => {
    if (!cur) return;
    const a = isLink ? elLink.current : el.current, other = isLink ? el.current : elLink.current;
    other?.pause();
    if (!isLink) {
      audio();
      if (wired !== a) {
        const c = audio();
        const srcNode = c.createMediaElementSource(a);
        analyser = c.createAnalyser(); analyser.fftSize = 1024; analyser.smoothingTimeConstant = 0.8;
        srcNode.connect(analyser).connect(c.destination);
        wired = a;
      }
    }
    a.src = urlOf(cur);
    a.play().then(() => setPlaying(true), err => { if (err.name !== 'AbortError') toast(`Couldn't play "${cur.title}". ${isLink ? 'That link may not be a sound file, or the site blocks playing it elsewhere.' : 'The music service may be busy.'}`, { kind: 'error' }); setPlaying(false); });
    setLyrics({ state: 'loading' });
    let alive = true;
    const find = d => api(`/api/music/lyrics?track=${encodeURIComponent(cur.title)}&artist=${encodeURIComponent(cur.artist && cur.src !== 'drive' && cur.src !== 'link' ? cur.artist : '')}&duration=${Math.round(d || cur.duration || 0)}`)
      .then(r => { if (alive) setLyrics({ lines: r.synced ? parseLRC(r.synced) : null, plain: r.plain, state: r.synced || r.plain ? 'ok' : 'none' }); })
      .catch(() => { if (alive) setLyrics({ state: 'none' }); });
    find(cur.duration);
    return () => { alive = false; };
  }, [idx, queue[idx]?.id]);

  // the visualizer + lyric follower, every frame
  useEffect(() => {
    let raf = 0;
    const freq = new Uint8Array(512), wave = new Uint8Array(1024);
    const loop = () => {
      raf = requestAnimationFrame(loop);
      const cv = canvas.current;
      if (!cv) return;
      const r = cv.getBoundingClientRect(), dpr = Math.min(2, devicePixelRatio || 1);
      if (cv.width !== Math.round(r.width * dpr)) { cv.width = Math.round(r.width * dpr); cv.height = Math.round(r.height * dpr); }
      const c = cv.getContext('2d');
      const p = player();
      if (analyser && !isLink) { analyser.getByteFrequencyData(freq); analyser.getByteTimeDomainData(wave); }
      else {
        // a link we can't read: a gentle made-up pulse while it plays
        const t = performance.now() / 1000, on = p && !p.paused ? 1 : 0;
        for (let i = 0; i < freq.length; i++) freq[i] = on * Math.max(0, 150 + 80 * Math.sin(t * 2 + i * 0.07) - i * 0.5) * (0.6 + 0.4 * Math.sin(t * 5.3));
        for (let i = 0; i < wave.length; i++) wave[i] = 128 + on * 40 * Math.sin(t * 6 + i * 0.05) * Math.sin(t * 1.3);
      }
      const css = getComputedStyle(document.documentElement);
      const col = { a: css.getPropertyValue('--a').trim() || '#3fb950', b: '#db61a2', fg: css.getPropertyValue('--fg').trim() || '#fff' };
      c.save(); c.scale(dpr, dpr);
      (VISUALIZERS.find(v => v.id === viz) || VISUALIZERS[0]).draw(c, r.width, r.height, freq, wave, vizState.current, col);
      c.restore();
    };
    loop();
    return () => cancelAnimationFrame(raf);
  }, [viz, isLink]);

  const lineAt = lyrics?.lines ? Math.max(-1, lyrics.lines.findIndex((l, i) => time >= l.t && (i === lyrics.lines.length - 1 || time < lyrics.lines[i + 1].t))) : -1;
  useEffect(() => {
    const box = lyricBox.current, row = box?.querySelector('.ly-line.now');
    if (row) box.scrollTo({ top: row.offsetTop - box.clientHeight / 2 + row.clientHeight / 2, behavior: 'smooth' });
  }, [lineAt]);

  const events = {
    onTimeUpdate: e => setTime(e.target.currentTime),
    onLoadedMetadata: e => setDur(e.target.duration),
    onPlay: () => setPlaying(true), onPause: () => setPlaying(false), onEnded: next,
  };

  const playLink = e => {
    e.preventDefault();
    let u;
    try { u = new URL(link.trim()); } catch { toast('That doesn\'t look like a link.'); return; }
    if (/youtube\.com|youtu\.be|spotify\.com|music\.apple/.test(u.hostname)) { toast('Links to YouTube, Spotify or Apple Music can\'t be played here. Use a direct link to a sound file (ending in .mp3 and so on), or upload the song.', { ms: 8000 }); return; }
    if (u.protocol !== 'https:') { toast('Use an https:// link.'); return; }
    const name = decodeURIComponent(u.pathname.split('/').pop() || u.hostname).replace(AUDIO, '') || 'Song';
    playTrack({ src: 'link', id: u.href, url: u.href, title: name, artist: u.hostname, art: '', duration: 0 });
    setLink('');
  };

  const row = (t, i, list) => (
    <li class={`docrow mu-track ${cur && cur.src === t.src && cur.id === t.id ? 'mu-cur' : ''}`} key={`${t.src}:${t.id}:${i}`}>
      <button type="button" class="docrow-main" onClick={() => playTrack(t, list)}>
        {t.art ? <img class="mu-art" src={t.art} alt="" loading="lazy" /> : <span class="docrow-icon"><Icon name="music" size={20} /></span>}
        <span class="docrow-text"><span class="docrow-title">{t.title}</span><span class="docrow-snippet">{[t.artist, t.duration ? fmtTime(t.duration) : '', SRC_NAME[t.src]].filter(Boolean).join(' · ')}</span></span>
      </button>
      <IconButton icon="plus" label="Add to queue" onClick={() => addToQueue(t)} />
    </li>
  );

  return (
    <div class="mu-player-wrap">
      <section class={`mu-card mu-stage ${full ? 'full' : ''}`}>
        <canvas ref={canvas} class="mu-viz" aria-hidden="true" />
        <div class="mu-stage-top">
          <div class="mu-vizpick" role="group" aria-label="Visualizer">
            {VISUALIZERS.map(v => <button type="button" key={v.id} class={`chip ${viz === v.id ? 'on' : ''}`} onClick={() => { setViz(v.id); vizState.current = {}; try { localStorage.setItem(VKEY, v.id); } catch { /* fine */ } }}>{v.name}</button>)}
          </div>
          <IconButton icon={full ? 'close' : 'open'} label={full ? 'Exit full screen' : 'Full screen'} onClick={() => setFull(!full)} />
        </div>
        <div class="mu-lyrics" ref={lyricBox} aria-live="off">
          {!cur ? <p class="ly-empty">Search for a song, paste a link, or play one of yours.</p>
            : lyrics?.state === 'loading' ? <p class="ly-empty">Looking for lyrics…</p>
              : lyrics?.lines ? lyrics.lines.map((l, i) => <p key={i} class={`ly-line ${i === lineAt ? 'now' : i < lineAt ? 'past' : ''}`} onClick={() => { const p = player(); if (p) p.currentTime = l.t; }}>{l.text || '♪'}</p>)
                : lyrics?.plain ? lyrics.plain.split('\n').map((l, i) => <p key={i} class="ly-line plain">{l}</p>)
                  : <p class="ly-empty">No lyrics found for this one (it may be instrumental).</p>}
        </div>
        <div class="mu-transport">
          <div class="mu-np">{cur?.art && <img class="mu-art" src={cur.art} alt="" />}<div><b>{cur ? cur.title : 'Nothing playing'}</b><span>{cur ? [cur.artist, SRC_NAME[cur.src]].filter(Boolean).join(' · ') : ''}</span></div></div>
          <div class="mu-btns">
            <IconButton icon="back" label="Previous" onClick={prev} disabled={!cur} />
            <button type="button" class="mu-play small" aria-label={playing ? 'Pause' : 'Play'} disabled={!cur} onClick={() => { const p = player(); if (!p) return; p.paused ? p.play() : p.pause(); }}><Icon name={playing ? 'pause' : 'play'} size={22} /></button>
            <IconButton icon="chevron" label="Next" onClick={next} disabled={idx + 1 >= queue.length} />
          </div>
          <div class="mu-seek"><span>{fmtTime(time)}</span>
            <input type="range" min="0" max={dur || 0} step="0.5" value={time} disabled={!cur || !dur} aria-label="Seek" onInput={e => { const p = player(); if (p) p.currentTime = +e.target.value; }} />
            <span>{fmtTime(dur)}</span></div>
        </div>
        <audio ref={el} crossOrigin="anonymous" preload="auto" {...events} />
        <audio ref={elLink} preload="auto" {...events} />
      </section>

      <section class="mu-card mu-find">
        <form class="mu-search" onSubmit={search}>
          <Icon name="search" size={17} />
          <input type="search" placeholder="Search free music (songs, artists, genres)" value={q} onInput={e => setQ(e.target.value)} aria-label="Search music" />
          <Button kind="primary" size="sm" type="submit" disabled={searching || !q.trim()}>{searching ? 'Searching…' : 'Search'}</Button>
        </form>
        <div class="mu-addrow">
          <form class="mu-link" onSubmit={playLink}>
            <input class="input" type="url" placeholder="Or paste a link to a song (https://…/song.mp3)" value={link} onInput={e => setLink(e.target.value)} aria-label="Song link" />
            <Button size="sm" type="submit" disabled={!link.trim()}>Play link</Button>
          </form>
          <input type="file" accept="audio/*" multiple hidden ref={fileIn} onChange={async e => { const f = [...e.target.files]; e.target.value = ''; if (f.length) { await upload(f); } }} />
          <Button size="sm" icon="upload" onClick={() => fileIn.current.click()}>Upload songs</Button>
        </div>
        {failed.length > 0 && <p class="hint">{failed.join(' and ')} didn't answer; showing the rest.</p>}
        {results && (results.length ? <><h3 class="drive-h">Results</h3><ul class="doclist">{results.map((t, i) => row(t, i, results))}</ul></> : <p class="hint">Nothing found for that. Try an artist or a genre (lofi, jazz, rock…).</p>)}
        {queue.length > 0 && <><h3 class="drive-h">Up next</h3><ul class="doclist">{queue.map((t, i) => (i > idx ? row(t, i, queue) : null)).filter(Boolean)}</ul>{idx >= queue.length - 1 && <p class="hint">The queue is empty after this song.</p>}</>}
        <h3 class="drive-h">Your songs</h3>
        {myFiles.length ? <ul class="doclist">{myFiles.map((t, i) => row(t, i, myFiles))}</ul> : <p class="hint">Songs you upload (here or in Drive) show up here.</p>}
        <p class="hint mu-credits">Free music from Audius and the Internet Archive. Lyrics from LRCLIB.</p>
      </section>
    </div>
  );
}
